import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearError, sendOtp, verifyOtpLogin } from '../store/slices/authSlice';
import { AurieqLogo } from '../components/Brand/AurieqLogo';
import AuthPageShell from '../components/Auth/AuthPageShell';
import { BRAND } from '../constants/branding';
import { authCardSx, authFieldSx, gradientButtonSx } from '../constants/brandStyles';

const emailSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
});

const codeSchema = yup.object({
  code: yup
    .string()
    .required('Verification code is required')
    .matches(/^\d{4,8}$/, 'Enter the numeric code from your email'),
});

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const successMessage = location.state?.message as string | undefined;

  const [email, setEmail] = React.useState('');
  const [codeSent, setCodeSent] = React.useState(false);
  const [resendSeconds, setResendSeconds] = React.useState(0);
  const [devCode, setDevCode] = React.useState<string | null>(null);

  const emailForm = useForm<{ email: string }>({
    resolver: yupResolver(emailSchema),
    mode: 'onChange',
    defaultValues: { email: '' },
  });

  const codeForm = useForm<{ code: string }>({
    resolver: yupResolver(codeSchema),
    mode: 'onChange',
    defaultValues: { code: '' },
  });

  React.useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  React.useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const handleSendOtp = async (targetEmail: string) => {
    const result = await dispatch(sendOtp({ email: targetEmail, purpose: 'login' })).unwrap();
    setEmail(targetEmail);
    setCodeSent(true);
    setResendSeconds(60);
    if (result.dev_code) setDevCode(result.dev_code);
  };

  const onSendCode = emailForm.handleSubmit(async (data) => {
    try {
      await handleSendOtp(data.email);
    } catch (e) {
      console.error('Failed to send OTP:', e);
    }
  });

  const onResend = async () => {
    if (!email || resendSeconds > 0) return;
    try {
      await handleSendOtp(email);
    } catch (e) {
      console.error('Failed to resend OTP:', e);
    }
  };

  const onVerify = codeForm.handleSubmit(async (data) => {
    try {
      await dispatch(verifyOtpLogin({ email, code: data.code })).unwrap();
    } catch (e) {
      console.error('OTP login failed:', e);
    }
  });

  return (
    <AuthPageShell>
      <Box sx={authCardSx}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <AurieqLogo variant="full" height={72} to="/" />
        </Box>
        <Typography variant="h5" align="center" gutterBottom sx={{ color: BRAND.colors.text }}>
          Sign in with email verification
        </Typography>

        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {devCode && <Alert severity="info" sx={{ mb: 2 }}>Development code: {devCode}</Alert>}

        {!codeSent && (
          <Box component="form" onSubmit={onSendCode} noValidate>
            <TextField
              {...emailForm.register('email')}
              fullWidth
              label="Email Address"
              type="email"
              margin="normal"
              autoFocus
              error={!!emailForm.formState.errors.email}
              helperText={emailForm.formState.errors.email?.message}
              sx={authFieldSx}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ ...gradientButtonSx, mt: 3, mb: 2 }}
              disabled={!emailForm.formState.isValid || loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send verification code'}
            </Button>
          </Box>
        )}

        {codeSent && (
          <Box component="form" onSubmit={onVerify} noValidate>
            <TextField fullWidth label="Email" value={email} margin="normal" InputProps={{ readOnly: true }} sx={authFieldSx} />
            <TextField
              {...codeForm.register('code')}
              fullWidth
              label="Verification code"
              margin="normal"
              autoFocus
              inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
              error={!!codeForm.formState.errors.code}
              helperText={codeForm.formState.errors.code?.message || 'Check your inbox for your code'}
              sx={authFieldSx}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ ...gradientButtonSx, mt: 3, mb: 1 }}
              disabled={!codeForm.formState.isValid || loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
            <Button fullWidth variant="text" onClick={onResend} disabled={loading || resendSeconds > 0} sx={{ color: BRAND.colors.textMuted }}>
              {resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code'}
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 2, borderColor: BRAND.colors.border }} />
        <Box textAlign="center">
          <Link component={RouterLink} to="/register" sx={{ color: BRAND.colors.primary }}>
            Don&apos;t have an account? Sign Up
          </Link>
        </Box>
        <Typography variant="caption" align="center" display="block" sx={{ mt: 2, color: BRAND.colors.textDim }}>
          <Link component={RouterLink} to="/privacy" sx={{ color: 'inherit' }}>Privacy</Link>
          {' · '}
          <Link component={RouterLink} to="/terms" sx={{ color: 'inherit' }}>Terms</Link>
        </Typography>
      </Box>
    </AuthPageShell>
  );
};

export default LoginPage;
